import { useState, useEffect, useContext, useReducer } from "react";
import { useNavigate } from 'react-router-dom';
import { useTable, usePagination } from 'react-table';
import Select from 'react-select';
import "../../css/ListUsers.css";
import Header from "../Navigation/Header";
import Footer from "../Navigation/Footer";
import SubsidiaryReadOnlyRow from "../../components/SubsidiaryReadOnlyRow";
import SubsidiaryModal from "../../components/SubsidiaryModal";
import FetchUtil from "../../utils/FetchUtil";
import UserContext from "../../contexts/UserContext";
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ddStyles } from "../../utils/CommonStyles.js";

const initialState = {
	queryPageIndex: 0,
	queryPageSize: 25,
	totalCount: 0,
	queryPageFilter: 0,
};

const PAGE_CHANGED = 'PAGE_CHANGED';
const TOTAL_COUNT_CHANGED = 'TOTAL_COUNT_CHANGED';
const PAGE_FILTER_CHANGED = 'PAGE_FILTER_CHANGED';

const reducer = (state, { type, payload, t }) => {
	switch (type) {
		case PAGE_CHANGED:
			return {
				...state,
				queryPageIndex: payload,
			};
		case TOTAL_COUNT_CHANGED:
			return {
				...state,
				totalCount: payload,
			};
		case PAGE_FILTER_CHANGED:
			return {
				...state,
				queryPageFilter: payload || 0,
				queryPageIndex: 0,
			};
		default:
			throw new Error(t("error.unhandledException") + " : ${type}");
	}
};

let prevCount = 0;
function setPrevCount(newCount) {
	prevCount = newCount;
}


function ListSubsidiaries() {
	const navigate = useNavigate();
	const [show, setShow] = useState(false);
	const [add, setAdd] = useState(false);
	const [errorValue, setErrorValue] = useState("");
	const [data, setData] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [companyOptions, setCompanyOptions] = useState([]);
	const [count, setCount] = useState(0);
	const [parentCompanyId, setParentCompanyId] = useState(0);
	const [companyLoaded, setCompanyLoaded] = useState(false);
	const [companySelected, setCompanySelected] = useState({ name: "companyId", label: "", value: 0 });
	const [useFilter, setUseFilter] = useState(false);
	const { t } = useTranslation();
	const userCtx = useContext(UserContext);

	const [columns, setColumns] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		fetchCompanies();
		setColumns([
			{ accessor: 'companyName', Header: t("page.name") },
			{ accessor: 'companyType', Header: t("page.company.type") },
			{ accessor: 'address', Header: t("page.address") },
			{ accessor: 'phone', Header: t("page.phone") },
			{ accessor: 'status', Header: t("page.status") },
			{ accessor: 'action', Header: t("page.action"), maxWidth: "10vw" },
		]);
	}, []);

	const initialValues = {
		companyId: 0,
		companyName: "",
		companyTypeKey: "",
		companyTypeDesc: "",
		address1: "",
		address2: "",
		phone: "",
		zipCode: "",
		zipExt: "",
		city: "",
		stateCode: "",
		countryCode: "US"
	};
	const [editFormData, setEditFormData] = useState(initialValues);

	function fetchCompanies() {
		setErrorValue("");
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ source: "web", getAll: false }),
		};
		try {
			FetchUtil.fetchAPI("FetchCompanies", "/Service/CompanyService/FetchCompanies", requestOptions, fetchCompaniesSuccess, fetchCompaniesFail);
		} catch (error) {
			throw new Error(error.message);
		}
	}

	function fetchCompaniesFail(redirect) {
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.companyListNotRetrieved"));
	}
	function fetchCompaniesSuccess(responseData) {
		let fetchCompaniesResponse;
		if (responseData) {
			fetchCompaniesResponse = responseData.fetchCompaniesResponse;
			if (fetchCompaniesResponse && fetchCompaniesResponse.errorCode === "0") {
				setCompanyValues(fetchCompaniesResponse.companies);
				setCompanies(fetchCompaniesResponse.companies);
			}  else if (fetchCompaniesResponse && fetchCompaniesResponse.errorCode === "114"){
				navigate("/",{
			        state: {
			            	errorMessage: t("error.sessionTimeout"),
			        	},
			     });
			} else if(fetchCompaniesResponse){
				setErrorValue(t("error.error") + " " + fetchCompaniesResponse.errorMessage);
			} else{
				setErrorValue(t("error.system.fetch"));
			}
		}
	}
	function setCompanyValues(companyObj) {
		let companyValue = {};
		let companyOptionList = [];
		companyOptionList.push({ label: "", value: 0 });
		if (companyObj && companyObj.length > 0) {
			companyObj.map((company, index) => {
				companyValue.label = company.companyName+ ', ' + (company.city? company.city + ', ': '') + company.stateCode + ', ' + company.countryCode;
				companyValue.value = company.companyId;
				companyOptionList.push(companyValue);
				companyValue = {};
			});
		}
		setCompanyOptions(companyOptionList);
		setCompanyLoaded(true);
	}

	const companyHandler = (selectedOption, action) => {
		let companyId = selectedOption ? selectedOption.value : 0;
		let companyName = selectedOption ? selectedOption.label : "";
		setUseFilter(false);
		if (companyId === parentCompanyId) {
			return
		}

		setParentCompanyId(companyId);
		setCompanySelected({ ...companySelected, label: companyName, value: companyId });
		if (companyId && companyId > 0) {
			setUseFilter(true);
			setAdd(true);
		} else {
			setData([]);
			setAdd(false);
		}
	}

	const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter }, dispatch] =
		useReducer(reducer, initialState);

	const { isError, error, isSuccess, refetch } = useQuery(
		['subsidiary-list', queryPageIndex, queryPageSize, queryPageFilter],
		() => retrieveSubsidiary(queryPageIndex, queryPageSize, queryPageFilter),
		{
			keepPreviousData: false,
			staleTime: 1000,
			enabled: useFilter,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		}
	);


	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		// Get the state from the instance
		state: { pageIndex, pageSize },
	} = useTable(
		{
			columns,
			data: isSuccess ? data : [],
			initialState: {
				pageIndex: queryPageIndex,
				pageSize: queryPageSize,
			},
			manualPagination: true,
			pageCount: isSuccess ? Math.ceil(count / queryPageSize) : null,
		},
		usePagination
	);

	useEffect(() => {
		dispatch({ type: PAGE_CHANGED, payload: pageIndex, t: t });
	}, [pageIndex]);

	useEffect(() => {
		if (count !== prevCount) {
			dispatch({
				type: TOTAL_COUNT_CHANGED,
				payload: count,
				t: t
			});

			setPrevCount(count);
		}
	}, [count]);

	useEffect(() => {
		if (useFilter) {
			dispatch({ type: PAGE_FILTER_CHANGED, payload: parentCompanyId, t: t });
			gotoPage(0);
		}

	}, [parentCompanyId, useFilter, gotoPage]);

	function retrieveSubsidiary(page, dispSize, parentCompanyInfo) {
		setErrorValue("");
		if (!parentCompanyInfo || parentCompanyInfo === 0) {
			return;
		}

		setIsLoading(true);
		const offset = page * dispSize;
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: true,
				offset: offset, limit: dispSize, companyId: parentCompanyInfo
			}),
		};
		try {
			FetchUtil.fetchAPI("GetSubsidiaries", "/Service/CompanyService/GetSubsidiaries", requestOptions, retrieveSubsidiarySuccess, retrieveSubsidiaryFail);
		} catch (error) {
			setIsLoading(false);
			throw new Error(error.message);
		}
	}

	function retrieveSubsidiaryFail(redirect) {
		setIsLoading(false);
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.subsidiaryNotRetrieved"));
	}

	function retrieveSubsidiarySuccess(responseData) {
		let getSubsidiariesResponse;
		if (responseData) {
			getSubsidiariesResponse = responseData.getSubsidiariesResponse;
			if (getSubsidiariesResponse && getSubsidiariesResponse.errorCode === "0") {
				setData(getSubsidiariesResponse.subsidiaryList.slice());
				if (getSubsidiariesResponse.offset === 0) {
					setCount(getSubsidiariesResponse.totalCount);
				}
			} else if (getSubsidiariesResponse && getSubsidiariesResponse.errorCode === "114"){
				navigate("/",{
			        state: {
			            	errorMessage: t("error.sessionTimeout"),
			        	},
			     });
			} else if(getSubsidiariesResponse){
				setErrorValue(t("error.error") + " " + getSubsidiariesResponse.errorMessage);
			} else{
				setErrorValue(t("error.system.fetch"));
			}
		}
		setIsLoading(false);
	}

	const handleAddClick = (event) => {
		event.preventDefault();
		setErrorValue("");
		setShow(true);
		setEditFormData(initialValues);
	}
	
	const handleDeleteClick = (event, subsidiaryCompanyId, statusCode) => {
		event.preventDefault();
		setErrorValue("");
		deleteSubsidiary(companySelected.value, subsidiaryCompanyId, statusCode);
	};

	
	const deleteSubsidiary = (parentCompanyInfo, subsidiaryCompanyId, statusCode) => {
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", parentCompanyId: parentCompanyInfo, subsidiaryCompanyId: subsidiaryCompanyId,
				statusCode: statusCode, updatedById: userCtx.loggedInUserId
			}),
		};
		try {
			FetchUtil.fetchAPI("DeleteSubsidiary", "/Service/CompanyService/DeleteSubsidiary", requestOptions, deleteSubsidiarySuccess, deleteSubsidiaryFail);
		} catch (error) {
			setErrorValue(error.message);
		}
	}

	function deleteSubsidiaryFail(redirect) {
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.subsidiaryDeleteFailed"));
	}
	function deleteSubsidiarySuccess(responseData) {
		let deleteSubsidiaryResponse;
		if (responseData) {
			deleteSubsidiaryResponse = responseData.deleteSubsidiaryResponse;
			if (deleteSubsidiaryResponse && deleteSubsidiaryResponse.errorCode === "0") {
				// only retrieve the modified user
				refetch();
			} else if (deleteSubsidiaryResponse) {
				refetch();
				setErrorValue(t("error.error") + " " + deleteSubsidiaryResponse.errorMessage);

			} else{
			 	setErrorValue(t("error.system.update"));
			}
		}
	}


	if (isError) {
		setErrorValue(t("error.error") + " " + error.message);
	}

	return (
		<div className="container-fluid">
			<Header />
			<div className={show ? "container-fluid container_list viewh-85 pe-none" : "container-fluid container_list viewh-85"}>
			{(userCtx.role === "SiteAdmin"  ) ?
				<>
				<div className="row">
					<div className="col">
						<div
							id="inerrorMessage"
							className={
								(errorValue) ? "errorblock" : "errorblock hide"
							}
						>
							{errorValue ? errorValue : ""}
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col">
						<div id="placeholder_1" className="placeholder-pg"></div>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<div className="form-group">
							<label className="form-label">
								<span className="form_required">*</span>
								{t("page.parentcompany")}
							</label>
							<Select
								name="companyId"
								options={companyOptions}
								onChange={companyHandler}
								placeholder={""}
								value={companySelected}
								isSearchable={companyLoaded}
								isClearable={companyLoaded}
								className="myselect"
								inputId="companyId-input"
								styles={ddStyles}
							/>
						</div>
					</div>
					{(isLoading) && (<div> <span class="spinner-border spinner-border-sm spinner-lg-size"></span> </div>)}
				</div>
				<div className="row">
					<div className="col">
						<div id="placeholder_2" className="placeholder-pg"></div>
					</div>
				</div>
				{add &&
					<div className="row">
						<div className="col">
							<button className="btn btn-primary btn-block me-4" data-toggle="modal" data-target="#subsidiaryModal"
								onClick={(event) => handleAddClick(event)}>{t("page.subsidiary.add")}</button>
						</div>
					</div>
				}
				<div className="row">
					<div className="col">
						{isSuccess ? (
							<>
								<table className="list-table" {...getTableProps()}>
									<thead>
										{headerGroups.map(headerGroup => (
											<tr {...headerGroup.getHeaderGroupProps()}>
												{headerGroup.headers.map(column => (
													<th className="list-th" {...column.getHeaderProps()}>{column.render("Header")}</th>
												))}
											</tr>
										))}
									</thead>
									<tbody {...getTableBodyProps()}>
										{data.length > 0 && (
											data.map((subsidiary, index) => (
												(subsidiary) &&
												<SubsidiaryReadOnlyRow
													t={t}
													subsidiary={subsidiary}
													handleDeleteClick={handleDeleteClick}
													oddIdx={((index % 2) > 0) ? true : false}
												/>
											))
										)}
									</tbody>

								</table>

								<div className="pagination">
									<button className="nav-button" onClick={() => { gotoPage(0); }} disabled={!canPreviousPage}>
										{'<<'}
									</button>
									<button className="nav-button" onClick={() => { previousPage(); }} disabled={!canPreviousPage}>
										{'<'}
									</button>
									<button className="nav-button" onClick={() => { nextPage(); }} disabled={!canNextPage}>
										{'>'}
									</button>
									<button className="nav-button"
										onClick={() => { gotoPage(pageCount - 1); }}
										disabled={!canNextPage}
									>
										{'>>'}
									</button>
									<span className="nav-page-info">{t("page.page") + ' '}
										<strong>
											{pageOptions.length > 0 ? pageIndex + 1 : 0} {t("page.of")} {pageOptions.length}
										</strong>
									</span>
									<span className="nav-page-goto">{t("page.goToPage")}:{' '}
										<input
											type="number"
											value={pageIndex + 1}
											onChange={(e) => {
												const page = e.target.value ? Number(e.target.value) - 1 : 0;
												gotoPage(page);
											}}
											className="nav-goto-input"
										/>
									</span>
								</div>
							</>
						) : null}
					</div>
				</div>
				{add &&
					<div className="row">
						<div className="col">
							<button className="btn btn-primary btn-block me-4" data-toggle="modal" data-target="#subsidiaryModal"
								onClick={(event) => handleAddClick(event)}>{t("page.subsidiary.add")}</button>
						</div>
					</div>
				}
				<div className="row">
					<div className="col">
						<div
							id="berrorMessage"
							className={
								(errorValue) ? "errorblock" : "errorblock hide"
							}
						>
							{errorValue ? errorValue : ""}
						</div>
					</div>
				</div>
				</>
			: null}
			</div>
			<div className="footer_container">
				<Footer />
			</div>
			{show && <SubsidiaryModal t={t} companyList={companies} parentCompanyId={parentCompanyId} setShow={setShow} refetch={refetch} />}
		</div>
	);
}

export default ListSubsidiaries;